import React, { useMemo, useState } from "react";
import { Row, Col } from "reactstrap";

import SideBar from "../../components/SideBar";
import Header from "../../components/Evaluation/Header";
import MonthlyBudgetChart from "../../components/Evaluation/MonthlyBudgetChart";

import { EVALUATION_TYPES, EVALUATION_YEARS, getMockEvaluationData } from "../../components/Evaluation/mockData";

function formatMAD(value) {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(value);
}

function Evaluation() {
  const [selectedType, setSelectedType] = useState(EVALUATION_TYPES[0].key);
  const [selectedYear, setSelectedYear] = useState(EVALUATION_YEARS[0]);

  const evaluationData = useMemo(
    () => getMockEvaluationData(selectedType, selectedYear),
    [selectedType, selectedYear]
  );

  const activeType = EVALUATION_TYPES.find(t => t.key === selectedType);
  const isOverBudget = evaluationData.restBudget < 0;
  const consumedPercent = Math.min(100, Math.round((evaluationData.consomedBudget / evaluationData.allowedBudget) * 100));
  const overshootPercent = Math.round((evaluationData.consomedBudget / evaluationData.allowedBudget - 1) * 100);

  return (
    <section className="evaluationSection">
      <div className="contents">
        <Row>
          <Col md="2" className="p-0">
            <SideBar />
          </Col>
          <Col md="10" className="p-0">
            <Header />

            <div className="pageContents">
              <div className="evaluationToolbar">
                <div className="typePicker">
                  {EVALUATION_TYPES.map(type => (
                    <button
                      type="button"
                      key={type.key}
                      className={`typeTile${selectedType === type.key ? " active" : ""}`}
                      onClick={() => setSelectedType(type.key)}
                    >
                      <span className="typeIcon">{type.icon}</span>
                      <span className="typeLabel">{type.label}</span>
                    </button>
                  ))}
                </div>

                <div className="yearPicker">
                  {EVALUATION_YEARS.map(year => (
                    <button
                      type="button"
                      key={year}
                      className={`yearChip${selectedYear === year ? " active" : ""}`}
                      onClick={() => setSelectedYear(year)}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              <div className="evaluationPanel">
                <div className="panelHeader">
                  <span className="panelEyebrow">Évaluation {selectedYear}</span>
                  <h2>{activeType.label}</h2>
                </div>

                <div className="kpiRow">
                  <div className="kpiTile">
                    <span className="kpiLabel">Budget alloué</span>
                    <span className="kpiValue">{formatMAD(evaluationData.allowedBudget)} <small>DH</small></span>
                  </div>
                  <div className="kpiTile">
                    <span className="kpiLabel">Budget consommé</span>
                    <span className="kpiValue">{formatMAD(evaluationData.consomedBudget)} <small>DH</small></span>
                  </div>
                  <div className={`kpiTile${isOverBudget ? " negative" : " positive"}`}>
                    <span className="kpiLabel">{isOverBudget ? "Dépassement" : "Reste disponible"}</span>
                    <span className="kpiValue">{formatMAD(Math.abs(evaluationData.restBudget))} <small>DH</small></span>
                  </div>
                </div>

                <div className="budgetThermometer">
                  <div className="thermometerTrack">
                    <div className={`thermometerFill${isOverBudget ? " over" : ""}`} style={{ width: `${consumedPercent}%` }}></div>
                  </div>
                  <span className="thermometerLabel">
                    {isOverBudget
                      ? `Budget dépassé de ${overshootPercent} %`
                      : `${consumedPercent}% du budget consommé`}
                  </span>
                </div>

                <div className="monthlySection">
                  <span className="sectionEyebrow">Consommation mensuelle</span>
                  <MonthlyBudgetChart
                    data={evaluationData.dataByMonth}
                    monthlyAllowed={evaluationData.averageAllowedBudgetByMonth}
                  />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
}

export default Evaluation;
